import {
  IElementBase,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";

/** All supported type of icons associated with elements */
export enum ElementIconType {
  DisabledIcon = 0,
  ClockIcon = 1,
  MapIcon = 2,
  PanoramaIcon = 3,
  FolderIcon = 4,
  LocationOnIcon = 5,
  PanoramaPhotosphereSelectIcon = 6,
  ImageIcon = 7,
  AccessTimeFilledIcon = 9,
  LinkIcon = 10,
  InfoIcon = 11,
  VolumeUpIcon = 12,
  AttachmentIcon = 13,
  SquareFootIcon = 14,
  StraightenIcon = 15,
  CloudIcon = 16,
  PointCloudIcon = 17,
  DeleteIcon = 18,
  DownloadIcon = 19,
  AlignToPointCloudIcon = 20,
  RegistrationInteractiveIcon = 21,
  ToolIcon = 22,
  VideoCameraIcon = 23,
  AlignToSheetIcon = 24,
  ResetScaleIcon = 25,
  DataSession = 26,
  DataSessionWithPano = 27,
  DataSessionWithTrajectory = 28,
  DataSessionVideoRecording = 29,
  CadModel = 30,
  Visible = 31,
  NonVisible = 32,
  AlignToModelIcon = 33,
  GeoReferenceIcon = 34,
  FindCheckmarkIcon = 35,
  WorldCoordinateIcon = 36,
  EditIcon = 37,
  MoveElement = 38,
  AlignIcon = 39,
  SheetNewIcon = 40,
  AreaIcon = 41,
  OverviewMapIcon = 42,
  CoordinatesIcon = 43,
  CoordinatesGearIcon = 44,
  ScanIcon = 45,
  ExportIcon = 46,
  LocationMarkerIcon = 47,
  ScanFlashIcon = 48,
  ScanMobileIcon = 49,
  ScanClusterIcon = 50,
  ScanClusterLockIcon = 51,
  PanoramaImageIcon = 52,
  UploadIcon = 53,
  AddDataIcon = 54,
  AssignTagsIcon = 55,
  TagsAddIcon = 56,
  MultiScanIcon = 57,
  DroneScanIcon = 58,
  CreateWaypointIcon = 59,
  CopyIcon = 60,
}

/**
 * Types of room section (location) based on its capture contents
 */
export enum LocationTypes {
  Photo360 = "Location with 360 photos only",
  Photo2D = "Location with 2D photos only",
  Photo360And2D = "Location with mixed 360 and 2D photos",
  LocationMarker = "Location marker",
  VideoMode = "Location obtained from a videomode",
}

/**
 * Compute the best icon for an IElement node
 *
 * @param elementType Type of the element
 * @param elementTypeHint Type hint of the element
 * @param childrenTypeHints List of type hints of the children elements
 * @param parentType Type of the element parent
 * @param parentTypeHint TypeHint of the element parent
 * @param locationType location type for room section
 * @returns the icon to use for this element
 */
export function iconForElement(
  elementType: IElementBase["type"],
  elementTypeHint: IElementBase["typeHint"],
  childrenTypeHints?: Array<IElementBase["typeHint"]>,
  parentType?: IElementBase["type"],
  parentTypeHint?: IElementBase["typeHint"],
  locationType?: LocationTypes,
): ElementIconType {
  if (locationType) {
    switch (locationType) {
      case LocationTypes.LocationMarker:
        return ElementIconType.LocationMarkerIcon;
      case LocationTypes.Photo360And2D:
        return ElementIconType.PanoramaImageIcon;
      case LocationTypes.Photo2D:
        return ElementIconType.ImageIcon;
      case LocationTypes.Photo360:
        return ElementIconType.PanoramaIcon;
      case LocationTypes.VideoMode:
        return ElementIconType.DataSessionVideoRecording;
    }
  }

  if (
    elementType === IElementType.section &&
    (elementTypeHint === IElementTypeHint.dataSession ||
      elementTypeHint === IElementTypeHint.dataSetFocus ||
      elementTypeHint === IElementTypeHint.dataSetEls ||
      elementTypeHint === IElementTypeHint.dataSetCapture)
  ) {
    return findIconForSession(childrenTypeHints);
  }
  if (
    parentType === IElementType.section &&
    parentTypeHint === IElementTypeHint.dataSets
  ) {
    // Assume "element" is a data set in this case
    return findIconForSession([elementTypeHint]);
  }
  if (
    parentType === IElementType.timeSeries &&
    parentTypeHint === IElementTypeHint.room
  ) {
    return ElementIconType.ClockIcon;
  }
  if (elementType === IElementType.model3dStream) {
    return ElementIconType.CadModel;
  }
  if (
    elementType === IElementType.imgSheet ||
    elementType === IElementType.imgSheetTiled
  ) {
    if (elementTypeHint === IElementTypeHint.area) {
      return ElementIconType.MapIcon;
    } else if (elementTypeHint === IElementTypeHint.overviewMap) {
      return ElementIconType.OverviewMapIcon;
    }
  }
  if (
    elementType === IElementType.section &&
    elementTypeHint === IElementTypeHint.timeTravel
  ) {
    return ElementIconType.PanoramaIcon;
  }

  if (
    elementType === IElementType.section &&
    (elementTypeHint === IElementTypeHint.focusScan ||
      elementTypeHint === IElementTypeHint.elsScan ||
      elementTypeHint === IElementTypeHint.orbisScan ||
      elementTypeHint === IElementTypeHint.focusPanoScan ||
      elementTypeHint === IElementTypeHint.focusPanoCalibrationScan ||
      elementTypeHint === IElementTypeHint.siteScapeScan ||
      elementTypeHint === IElementTypeHint.unstructuredScan ||
      elementTypeHint === IElementTypeHint.roviScan)
  ) {
    return ElementIconType.ScanIcon;
  }

  if (
    elementType === IElementType.section &&
    elementTypeHint === IElementTypeHint.photogrammetryScan
  ) {
    return ElementIconType.DroneScanIcon;
  }

  if (
    elementType === IElementType.section &&
    elementTypeHint === IElementTypeHint.orbisFlashScan
  ) {
    return ElementIconType.ScanFlashIcon;
  }

  // SiteScape capture clusters should be represented as a scan even though they have children scans
  if (
    elementType === IElementType.cluster &&
    elementTypeHint === IElementTypeHint.siteScapeCapture
  ) {
    return ElementIconType.ScanIcon;
  }

  if (
    (elementType === IElementType.section &&
      elementTypeHint === IElementTypeHint.cluster) ||
    elementType === IElementType.cluster
  ) {
    return ElementIconType.ScanClusterIcon;
  }

  switch (elementTypeHint) {
    case IElementTypeHint.area:
      return ElementIconType.AreaIcon;
    case IElementTypeHint.room:
      return ElementIconType.PanoramaIcon;
    case IElementTypeHint.dataSession:
      return ElementIconType.FolderIcon;
    case IElementTypeHint.odometryPath:
      return ElementIconType.ClockIcon;
    case IElementTypeHint.videoRecordings:
      return elementType === IElementType.section
        ? ElementIconType.DataSessionVideoRecording
        : ElementIconType.FolderIcon;
    case IElementTypeHint.bimModel:
      return elementType === IElementType.section
        ? ElementIconType.CadModel
        : ElementIconType.FolderIcon;
  }

  switch (elementType) {
    case IElementType.projectRoot:
    case IElementType.group:
      return ElementIconType.FolderIcon;
    case IElementType.section:
      return ElementIconType.LocationOnIcon;
    case IElementType.img360:
    case IElementType.imgCube:
      return ElementIconType.PanoramaPhotosphereSelectIcon;
    case IElementType.imgSheet:
      return ElementIconType.ImageIcon;
    case IElementType.img2d:
      return ElementIconType.ClockIcon;
    case IElementType.model3d:
      return ElementIconType.CadModel;
    case IElementType.timeSeries:
      return ElementIconType.AccessTimeFilledIcon;
    case IElementType.iElemLink:
    case IElementType.urlLink:
      return ElementIconType.LinkIcon;
    case IElementType.markup:
      return ElementIconType.InfoIcon;
    case IElementType.audioAttachment:
      return ElementIconType.VolumeUpIcon;
    case IElementType.attachment:
      return ElementIconType.AttachmentIcon;
    case IElementType.measureWall:
      return ElementIconType.SquareFootIcon;
    case IElementType.measureLine:
      return ElementIconType.StraightenIcon;
    case IElementType.pCloud:
    case IElementType.pointCloudStream:
    case IElementType.pointCloudStreamWebShare:
      return ElementIconType.CloudIcon;
    default:
      return ElementIconType.SquareFootIcon;
  }
}
function findIconForSession(
  childrenTypeHints: Array<IElementBase["typeHint"]> | undefined,
): ElementIconType {
  if (childrenTypeHints?.includes(IElementTypeHint.dataSetGeoSlam)) {
    return ElementIconType.DataSessionWithTrajectory;
  } else if (childrenTypeHints?.includes(IElementTypeHint.dataSetVideoWalk)) {
    return ElementIconType.DataSessionVideoRecording;
  }

  return ElementIconType.DataSession;
}
